/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useMemo, useRef, useCallback } from "react";
import { ApolloError } from "@apollo/client";
import { Else, If, Then, When } from "react-if";
import { Box, Col, FlexContainer, Row, theme } from "@nordcloud/gnui";
import {
  EstateV2Filter,
  Provider,
  SearchSortField,
  SearchSortOrder,
} from "~/generated/graphql";
import {
  LoaderWrap,
  Pagination,
  ReactTable,
  ErrorWrap,
  SearchBoxContainer,
} from "~/components";
import { EstateData } from "~/components/EstateRecordsList";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateSort,
  useFilteredQueryState,
  useQueryStateWithFilters,
  useScrollAfterPaging,
} from "~/hooks";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  AddSearchPresetModal,
  EstateActiveFilters,
  EstateFiltersToggle,
  EstateRecordsLoader,
  EstateSort,
  Export,
} from "./components";
import {
  disabledFilterFields,
  DisabledFilters,
} from "./components/EstateFilters";
import { useAddSearchPreset, useGetSearchPresets } from "./components/hooks";
import { NewResource } from "./EstateDetailsPage/components/Ec2CommonTabs/types";
import { EstateListNewColumns } from "./EstateList";
import {
  convertTags,
  filterEmptyValuesInObject,
  generateNewRowData,
  mapSearchPresetFilterToFilter,
} from "./helpers";

type SortingProp = {
  field?: SearchSortField;
  order?: SearchSortOrder;
};

type QueryState = EstateV2Filter &
  SortingProp & {
    page: number;
    limit: number;
    tags: string[];
    metadata: string[];
    tab: number;
    query: string;
  };

type SearchPreset = Parameters<
  NonNullable<
    React.ComponentProps<typeof EstateFiltersToggle>["onSearchPresetApply"]
  >
>[0];

type Props = {
  setSelectedResources?: (value: string[]) => void;
  selectedResources?: string[];
  envNid?: string;
  disabledFilters?: DisabledFilters[];
  excludeColumns?: string[];
  dataEstate: {
    data?: NewResource;
    loading: boolean;
    error?: ApolloError;
    refetch?: () => void;
  };
  allowSavingFilters?: boolean;
  queryFilters: React.ComponentProps<typeof Export>["filters"];
};

// TODO: Reduce amount of code
export function EstateRecords({
  setSelectedResources = () => null,
  selectedResources,
  envNid,
  dataEstate,
  queryFilters,
  disabledFilters = [],
  excludeColumns = [],
  allowSavingFilters = false,
}: Props) {
  const [isAddPresetModalOpen, setIsAddPresetModalOpen] = useState(false);
  const scrollToRef = useRef<HTMLDivElement>(null);

  const { currency } = useCurrency();

  const skipFields = disabledFilters.flatMap((disabledFilter) =>
    disabledFilterFields.get(disabledFilter)
  );
  const { state, updateQueryState, setQueryState } = useFilteredQueryState(
    estateV2FilterFields.filter(
      (filterField) => !skipFields.includes(filterField)
    )
  );

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page: ignoredPage,
    limit: ignoredLimit,
    field,
    order,
    tags,
    tab,
    query,
    ...rawFilters
  }: QueryState = state;
  const [searchInput, setSearchInput] = useState(query);

  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const filters = {
    ...rawFilters,
    tags: convertTags(tags),
    metadata: convertTags(rawFilters.metadata),
  };

  const { data, loading, error, refetch } = dataEstate;

  const { data: searchPresetData } = useGetSearchPresets({ page: 0, limit: 5 });

  const addSearchPreset = useAddSearchPreset();

  const rows = useMemo(() => {
    return (
      data?.estateV2?.resources?.map((res) => ({
        ...generateNewRowData(res, currency, envNid),
        refetch,
      })) ?? []
    );
  }, [data, refetch, currency, envNid]);

  const columns = useMemo(
    () =>
      EstateListNewColumns.filter(
        (column) => !excludeColumns.includes(column.accessor)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeColumns.length]
  );

  const hasResources = isNotEmpty(rows);
  const estateCount = data?.estateV2?.count ?? 0;
  const showPagination = estateCount > 0;

  const { activeFilters, removeFields, clearFilters } =
    useQueryStateWithFilters({
      tags,
      ...rawFilters,
    });
  const showFilters = isNotEmpty(activeFilters);

  const removeFilterValue = (key: keyof typeof filters, index: number) => {
    const newFilters = { tags, ...rawFilters, tab };

    const newKeyValue = newFilters[key];
    if (!Array.isArray(newKeyValue)) {
      return;
    }
    if (key === "provider" && newKeyValue[index] === Provider.External) {
      delete newFilters.externalIntegration;
    }
    newKeyValue.splice(index, 1);

    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    setQueryState(newFilters);
  };

  const selectedRowsHandler = useCallback(
    (items: string[]) => {
      setSelectedResources(
        items.filter(
          (item) =>
            data?.estateV2?.resources
              ?.filter((row) => row.__typename !== "CostResource")
              .map((row) => row.id)
              .includes(item)
        )
      );
    },
    [setSelectedResources, data?.estateV2?.resources]
  );

  const handleSubmit = async ({ name }: { name: string }) => {
    const formData = {
      name,
      sort: {
        field: sortField,
        order: sortDirection,
      },
      query,
      filter: filters,
    };

    return addSearchPreset(formData);
  };

  const handleApplySearchPreset = (searchPreset: SearchPreset) => {
    const { __typename, ...rest } = searchPreset.filter ?? {};
    setQueryState({
      ...filterEmptyValuesInObject(mapSearchPresetFilterToFilter(rest)),
      page: 0,
      ...(searchPreset.query && { query: searchPreset.query }),
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
      field: searchPreset.sort?.field ?? SearchSortField.Cost,
      order: searchPreset.sort?.order ?? SearchSortOrder.Dsc,
    });
  };

  useScrollAfterPaging({ scrollToRef, enabled: !loading });

  return (
    <div ref={scrollToRef}>
      <ErrorWrap
        error={error}
        status={hasResources ? ERROR_COLORS.notification : ERROR_COLORS.danger}
      >
        <Box boxStyle="lightGrey" spacing="spacing04">
          <FlexContainer
            justifyContent="space-between"
            alignContent="center"
            gap={theme.spacing.spacing05}
          >
            <EstateSort
              sortField={sortField}
              sortDirection={sortDirection}
              onSortSelect={onSortSelect}
              onSortOrderChange={onSortOrderChange}
            />
            <div
              css={{
                flexGrow: 1,
              }}
            >
              <SearchBoxContainer
                searchInput={searchInput}
                setSearchInput={setSearchInput}
              />
            </div>
            <EstateFiltersToggle
              disabledFilters={disabledFilters}
              searchPresets={searchPresetData?.searchPresets?.presets ?? []}
              allowSavingFilters={allowSavingFilters}
              isDisabled={loading}
              onApply={() => setSearchInput(query ?? "")}
              onSearchPresetApply={handleApplySearchPreset}
            />
            <Export
              filters={queryFilters}
              recordsCount={data?.estateV2?.count ?? 0}
            />
          </FlexContainer>
          <When condition={showFilters}>
            <Row>
              <Col md="12">
                <div
                  css={{
                    paddingTop: theme.spacing.spacing03,
                    marginTop: theme.spacing.spacing03,
                    borderTop: `1px solid ${theme.color.border.border01}`,
                  }}
                >
                  <EstateActiveFilters
                    filters={activeFilters}
                    allowSavingFilters={allowSavingFilters}
                    onRemoveKey={removeFields}
                    onClear={clearFilters}
                    onSave={() => setIsAddPresetModalOpen(true)}
                    onRemoveValue={(itemName, index) =>
                      removeFilterValue(itemName as keyof EstateV2Filter, index)
                    }
                  />
                </div>
              </Col>
            </Row>
          </When>
        </Box>
        <LoaderWrap loading={loading} Component={EstateRecordsLoader}>
          <If condition={hasResources}>
            <Then>
              <div css={{ marginBottom: theme.spacing.spacing04 }}>
                <EstateData
                  currentMonthTotal={data?.estateV2?.cost?.currentMonthTotal}
                  count={data?.estateV2?.count}
                />
              </div>
              <ReactTable
                striped
                columns={columns}
                data={rows}
                sort={false}
                deselectRows={isEmpty(selectedResources ?? [])}
                onSelectedRowsChange={selectedRowsHandler}
              />
              <When condition={showPagination}>
                <Pagination count={estateCount} />
              </When>
            </Then>
            <Else>
              <p
                css={{
                  paddingTop: theme.spacing.spacing04,
                  paddingBottom: theme.spacing.spacing04,
                }}
              >
                No resources matching selected criteria
              </p>
            </Else>
          </If>
        </LoaderWrap>
      </ErrorWrap>
      <AddSearchPresetModal
        isOpen={isAddPresetModalOpen}
        onSubmit={handleSubmit}
        onClose={() => setIsAddPresetModalOpen(false)}
      />
    </div>
  );
}
